/**
 * Cancellation policy tier editor (shared, web).
 *
 * Renders an editable list of refund tiers (`hoursBefore` + `refundPercent`)
 * with live validation (max 10 tiers, sorted strictly descending by
 * `hoursBefore`, last tier MUST be `hoursBefore=0`).
 *
 * Hoisted from `apps/portal` so BOTH the F12 portal venue-owner editor tab AND
 * the F42 admin venue-detail "Cancellation policy" tab compose the same control
 * over the shared `<PolicyTier>` primitive. Controlled component — the parent
 * owns `tiers` and composes it with a Save button + live `<CancelTierTrack>`
 * preview.
 *
 * Like `<CancelTierTrack>`, this stays `@bravemove/types`-free (ui-web has no
 * `@bravemove/types` dependency by design): the tier shape, the policy invariants,
 * and the platform-default tiers are mirrored locally. Keep `DEFAULT_TIERS` in
 * sync with `PLATFORM_DEFAULT_CANCELLATION_POLICY` in `@bravemove/types`. Reads i18n
 * via `@bravemove/i18n/react/core` (the DOM-free subset) so this file stays inside
 * `@bravemove/ui-web` without re-introducing the ui-web → i18n/react → ui-web cycle
 * (same rule as `ConnectedLanguageSwitcher`); the `cancellation:editor.*` /
 * `cancellation:policy.*` keys live in the shared `@bravemove/i18n` cancellation
 * namespace loaded by every web app.
 */
import { useMemo } from 'react';
import { useI18n } from '@bravemove/i18n/react/core';
import { Banner } from '../Banner';
import { Button } from '../Button';
import { PolicyTier } from '../PolicyTier';

const MAX_TIERS = 10;

/** Mirror of `PLATFORM_DEFAULT_CANCELLATION_POLICY.tiers` (@bravemove/types) — keep in sync. */
const DEFAULT_TIERS: ReadonlyArray<PolicyTierValue> = [
  { hoursBefore: 24, refundPercent: 100 },
  { hoursBefore: 6, refundPercent: 50 },
  { hoursBefore: 0, refundPercent: 0 },
];

/** Local mirror of `@bravemove/types` `CancellationPolicyTier` (ui-web is types-free). */
export interface PolicyTierValue {
  readonly hoursBefore: number;
  readonly refundPercent: number;
}

export interface CancellationPolicyEditorProps {
  readonly tiers: ReadonlyArray<PolicyTierValue>;
  readonly onChange: (next: ReadonlyArray<PolicyTierValue>) => void;
  readonly readOnly?: boolean;
}

/**
 * Validate a tier list against the policy invariants. Returns null when valid OR
 * an i18n key string the caller should translate. Mirrors `CancellationPolicySchema`
 * (@bravemove/types) — kept in sync; ui-web stays types-free.
 */
function validateTiers(tiers: ReadonlyArray<PolicyTierValue>): string | null {
  if (tiers.length < 1 || tiers.length > MAX_TIERS) {
    return 'cancellation:editor.validation_max_tiers';
  }
  if (tiers[tiers.length - 1]?.hoursBefore !== 0) {
    return 'cancellation:editor.validation_last_tier_zero';
  }
  for (let i = 1; i < tiers.length; i += 1) {
    if ((tiers[i - 1]?.hoursBefore ?? 0) <= (tiers[i]?.hoursBefore ?? 0)) {
      return 'cancellation:editor.validation_sort';
    }
  }
  return null;
}

/**
 * Live refund-preview at three illustrative hoursBefore points: 25h (catches
 * the top tier), 5h (catches the middle if one exists), 1h (catches the last).
 */
function previewFor(tiers: ReadonlyArray<PolicyTierValue>, hoursBefore: number): number {
  for (const t of tiers) {
    if (hoursBefore >= t.hoursBefore) return t.refundPercent;
  }
  return tiers[tiers.length - 1]?.refundPercent ?? 0;
}

export function CancellationPolicyEditor({
  tiers,
  onChange,
  readOnly = false,
}: CancellationPolicyEditorProps) {
  const { t } = useI18n();
  const validationKey = useMemo(() => validateTiers(tiers), [tiers]);

  const update = (i: number, patch: Partial<PolicyTierValue>) => {
    const next = tiers.map((tier, idx) => (idx === i ? { ...tier, ...patch } : tier));
    onChange(next);
  };

  const remove = (i: number) => {
    if (tiers.length <= 1) return;
    // The hoursBefore=0 tier is mandatory; refuse to drop the row whose value is 0.
    if (tiers[i]?.hoursBefore === 0) return;
    onChange(tiers.filter((_, idx) => idx !== i));
  };

  const add = () => {
    if (tiers.length >= MAX_TIERS) return;
    // Insert a new tier above the last (hoursBefore=0) tier with a sensible default.
    const lastIdx = tiers.length - 1;
    const prevHours = tiers[lastIdx - 1]?.hoursBefore ?? 48;
    const newHours = Math.max(1, Math.floor(prevHours / 2));
    const inserted: PolicyTierValue = { hoursBefore: newHours, refundPercent: 25 };
    onChange([...tiers.slice(0, lastIdx), inserted, tiers[lastIdx]!]);
  };

  const resetDefault = () => {
    onChange([...DEFAULT_TIERS]);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="cancellation-policy-editor">
      <div className="flex flex-col gap-2">
        {tiers.map((tier, i) => {
          const isMandatoryRow = tier.hoursBefore === 0;
          return (
            <div
              key={`${i}-${tier.hoursBefore}`}
              className="flex items-stretch gap-2"
              data-testid={`policy-tier-row-${i}`}
            >
              <div className="flex-1">
                <PolicyTier
                  hoursBefore={tier.hoursBefore}
                  refundPercent={tier.refundPercent}
                  readOnly={readOnly}
                  onChange={(next) => update(i, next)}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                disabled={readOnly || tiers.length <= 1 || isMandatoryRow}
                data-testid={`policy-tier-remove-${i}`}
              >
                {t('cancellation:editor.remove_tier')}
              </Button>
            </div>
          );
        })}
      </div>

      {validationKey ? (
        <div data-testid="policy-validation-banner">
          <Banner tone="warning" title={t(validationKey)} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={add}
          disabled={readOnly || tiers.length >= MAX_TIERS}
          data-testid="policy-add-tier"
        >
          {t('cancellation:editor.add_tier')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetDefault}
          disabled={readOnly}
          data-testid="policy-reset-default"
        >
          {t('cancellation:editor.reset_default')}
        </Button>
      </div>

      <section
        className="rounded-md border border-hairline bg-surface-2 p-3 flex flex-col gap-1"
        data-testid="policy-preview"
      >
        <h3 className="text-h3 font-bold text-ink">{t('cancellation:policy.preview_heading')}</h3>
        <ul className="text-[12.5px] text-ink-2 flex flex-col gap-0.5">
          <li>
            {t('cancellation:policy.tier_label', {
              hoursBefore: 25,
              refundPercent: previewFor(tiers, 25),
            })}
          </li>
          <li>
            {t('cancellation:policy.tier_label', {
              hoursBefore: 5,
              refundPercent: previewFor(tiers, 5),
            })}
          </li>
          <li>
            {t('cancellation:policy.tier_label', {
              hoursBefore: 1,
              refundPercent: previewFor(tiers, 1),
            })}
          </li>
        </ul>
      </section>
    </div>
  );
}
